import { Link } from "react-router-dom";
import Icon from "@/components/feature/Icon";
import { useSiteSettings } from "@/lib/siteSettings";

const footerColumns = [
  {
    title: "關於協會",
    links: [
      { label: "組織簡介", path: "/about" },
      { label: "成立宗旨", path: "/about" },
      { label: "理監事團隊", path: "/about" },
      { label: "協會章程", path: "/about" },
    ],
  },
  {
    title: "會員服務",
    links: [
      { label: "入會方式", path: "/membership#join" },
      { label: "會員權益", path: "/membership" },
      { label: "會員名錄", path: "/membership" },
      { label: "合作夥伴", path: "/membership" },
    ],
  },
  {
    title: "活動與消息",
    links: [
      { label: "近期活動", path: "/events" },
      { label: "最新消息", path: "/events" },
      { label: "活動回顧", path: "/events" },
      { label: "年度論壇", path: "/events" },
    ],
  },
  {
    title: "資源中心",
    links: [
      { label: "白皮書", path: "/resources" },
      { label: "研究報告", path: "/resources" },
      { label: "會刊", path: "/resources" },
      { label: "法規連結", path: "/resources" },
    ],
  },
  {
    title: "聯絡與支持",
    links: [
      { label: "聯絡表單", path: "/contact" },
      { label: "常見問題", path: "/contact" },
      { label: "支持協會", path: "/contact" },
      { label: "社群媒體", path: "/contact" },
    ],
  },
];

export default function Footer() {
  const { settings } = useSiteSettings();
  const { contact, social } = settings;

  const socialIcons = [
    { url: social.facebook_url, icon: "ri-facebook-fill", label: "Facebook", hover: "hover:bg-[#1877F2] hover:border-[#1877F2]" },
    { url: social.linkedin_url, icon: "ri-linkedin-fill", label: "LinkedIn", hover: "hover:bg-[#0A66C2] hover:border-[#0A66C2]" },
    { url: social.youtube_url, icon: "ri-youtube-fill", label: "YouTube", hover: "hover:bg-[#FF0000] hover:border-[#FF0000]" },
  ].filter((s) => s.url);

  return (
    <footer className="bg-silver-50 border-t border-silver-200">
      {/* Upper nav area */}
      <div className="px-6 md:px-10 lg:px-16 py-16 md:py-20">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-8 md:gap-10">
          {footerColumns.map((col) => (
            <div key={col.title}>
              <h4 className="text-navy-950 font-semibold text-sm md:text-base mb-4">
                {col.title}
              </h4>
              <div className="w-8 h-px bg-navy-950 mb-4" />
              <ul className="space-y-3">
                {col.links.map((link) => (
                  <li key={link.label}>
                    <Link
                      to={link.path}
                      className="text-silver-500 text-sm hover:text-navy-800 transition-colors"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* Bottom info area */}
      <div className="px-6 md:px-10 lg:px-16 pb-8 md:pb-12">
        <div className="border-t border-silver-200 pt-8 md:pt-12 flex flex-col md:flex-row items-start md:items-end justify-between gap-8">
          <div>
            <h3 className="text-4xl md:text-6xl lg:text-7xl font-bold text-navy-950 tracking-tight">
              TAIAA
            </h3>
            <p className="text-navy-950 font-semibold text-base md:text-lg mt-2">
              {contact.legal_name}
            </p>
            <div className="w-16 h-1 bg-navy-950 mt-3 mb-3" />
            <p className="text-silver-500 text-sm max-w-md leading-relaxed">
              推動 AI 應用落地，連結台灣產業未來。協助企業、專業人士、教育單位與創新團隊理解 AI、導入 AI、善用 AI。
            </p>
            <ul className="mt-4 space-y-1 text-sm text-silver-500">
              {contact.registration && <li>{contact.registration}</li>}
              <li>協會會址：{contact.address}</li>
              {contact.phone && <li>聯絡電話：{contact.phone}</li>}
              <li>
                連絡郵件：
                <a
                  href={`mailto:${contact.email}`}
                  className="hover:text-navy-800 transition-colors"
                >
                  {contact.email}
                </a>
              </li>
            </ul>
          </div>

          <div className="flex flex-col items-start md:items-end gap-4">
            <div className="flex items-center gap-5">
              <a
                href={`mailto:${contact.email}`}
                className="w-10 h-10 rounded-full border border-silver-300 flex items-center justify-center text-navy-950 hover:bg-navy-950 hover:text-white transition-colors"
                aria-label="Email"
              >
                <Icon className="ri-mail-line text-lg" />
              </a>
              {contact.community_url && (
                <a
                  href={contact.community_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full border border-silver-300 flex items-center justify-center text-navy-950 hover:bg-[#06C755] hover:text-white hover:border-[#06C755] transition-colors"
                  aria-label="LINE 社群"
                >
                  <Icon className="ri-chat-1-fill text-lg" />
                </a>
              )}
              {socialIcons.map((s) => (
                <a
                  key={s.label}
                  href={s.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`w-10 h-10 rounded-full border border-silver-300 flex items-center justify-center text-navy-950 hover:text-white transition-colors ${s.hover}`}
                  aria-label={s.label}
                >
                  <Icon className={`${s.icon} text-lg`} />
                </a>
              ))}
            </div>
            <p className="text-silver-400 text-xs">
              © {new Date().getFullYear()} TAIAA {contact.legal_name}. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}
